import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const cardRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    message: '',
    source: '',
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(leftRef.current.querySelectorAll('.contact-reveal'), {
        y: 30,
        opacity: 0,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.1,
        scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' },
      });

      gsap.from(cardRef.current, {
        y: 50,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form:', form);
  };

  const fieldBase =
    'w-full bg-gray-100 border-0 rounded-none px-4 py-3 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A35]/40 transition';

  return (
    <section ref={sectionRef} className="relative bg-white overflow-hidden">
      {/* Dark teal panel on right */}
      <div className="hidden lg:block absolute top-0 right-0 w-1/2 h-full bg-[#1a1a1d]" />
      {/* Small accent block top-right */}
      <div className="hidden lg:block absolute top-0 right-[50%] w-48 h-28 bg-[#1a1a1d]" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        {/* Left */}
        <div ref={leftRef} className="flex flex-col justify-center lg:pr-8">
          <span className="contact-reveal text-xs sm:text-sm font-bold tracking-[0.2em] text-[#F05A35] uppercase mb-5">
            Contact
          </span>
          <h2 className="contact-reveal text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-gray-900 mb-8">
            Talk to us
          </h2>
          <p className="contact-reveal text-base text-gray-700 leading-relaxed mb-4 max-w-md">
            Want to learn more about our salon services and treatments? Simply complete the form
            to arrange a call with a member of our team.
          </p>
          <p className="contact-reveal text-base text-gray-700 leading-relaxed mb-10 max-w-md">
            We aim to respond to every enquiry within{' '}
            <span className="font-bold">24 hours</span>.
          </p>

          <div className="contact-reveal border border-gray-200 p-6 sm:p-8 max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Call us on:</h3>
            <a
              href="tel:+256700123456"
              className="inline-flex items-center gap-2 text-[#F05A35] hover:text-[#d94a28] text-lg font-semibold transition-colors"
            >
              +256 700 123 456
              <ArrowRightIcon className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Right — Form card overlapping teal panel */}
        <div className="relative lg:pl-8">
          <div
            ref={cardRef}
            className="bg-white shadow-2xl p-6 sm:p-10 lg:-mr-4 lg:-mt-10 lg:mb-10"
          >
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              Get in touch with our team
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-8">
              Our friendly team is always on hand to answer any queries you may have about our
              services, treatments, or anything else you're curious about.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    First name <span className="text-[#F05A35]">*</span>
                  </label>
                  <input
                    required
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    className={fieldBase}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Last name <span className="text-[#F05A35]">*</span>
                  </label>
                  <input
                    required
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    className={fieldBase}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Company name <span className="text-[#F05A35]">*</span>
                </label>
                <input
                  required
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  className={fieldBase}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Phone number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className={fieldBase}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Email <span className="text-[#F05A35]">*</span>
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className={fieldBase}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Message</label>
                <textarea
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  className={`${fieldBase} resize-y`}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  How did you hear about us? <span className="text-[#F05A35]">*</span>
                </label>
                <textarea
                  required
                  name="source"
                  rows={3}
                  value={form.source}
                  onChange={handleChange}
                  className={`${fieldBase} resize-y`}
                />
              </div>

              <p className="text-xs text-gray-500 leading-relaxed">
                We will use your information to communicate any future events and services. You
                may unsubscribe at any time. For further details please review our{' '}
                <a href="/privacy" className="underline hover:text-[#F05A35]">
                  privacy policy
                </a>
                .
              </p>

              <button
                type="submit"
                className="group inline-flex items-center gap-3 bg-[#F05A35] hover:bg-[#d94a28] text-white font-semibold text-sm px-7 py-3.5 transition-colors"
              >
                Submit
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
